import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { VariantRulesEditor } from "../components/game/VariantRulesEditor";
import { WelcomeIntroModal } from "../components/game/WelcomeIntroModal";
import { RaidModePickerModal } from "../components/game/RaidModePickerModal";
import { GameShowIntroModal } from "../components/game/GameShowIntroModal";
import { ExpandableTitle } from "../components/game/ExpandableTitle";
import { WhisperInboxButton } from "../components/game/WhisperInboxButton";
import { TrialCountdownBanner } from "../components/game/TrialCountdownBanner";
import { DragonCompanion } from "../components/knightsfee/DragonCompanion";
import { toast } from "sonner";
import { Plus, Swords, Copy, ScrollText, Crown, Ticket, Images, Shield, Disc3, Store, Lock, Settings } from "lucide-react";

const TRIAL_LABEL = { trialing: "Trial Active", active: "Subscribed", past_due: "Payment Failed", canceled: "Plan Canceled" };
const RAID_MODE_LABELS = { alliance: "Alliance", tournament: "Tournament", party: "Party", bbebbeg: "BBEBBEG", dungeon_crawl: "Dungeon Crawl" };
const KF_NAV_ICON_URL = "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/6da21ed8f2bf8dcbc603984f36a122f1ce66cc46a43e8a4d278f542275ef3c68.jpeg";
const DASHBOARD_BG_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_adventure-hub-220/artifacts/z6xhgc7x_main%20lobby.png";

export default function DashboardPage() {
  const { user, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [renewingId, setRenewingId] = useState(null);
  const [name, setName] = useState("");
  const [gridType, setGridType] = useState("square");
  const [system, setSystem] = useState("pf2e");
  const [variantRules, setVariantRules] = useState({});
  const [joinCode, setJoinCode] = useState("");
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [raids, setRaids] = useState([]);
  const [raidName, setRaidName] = useState("");
  const [raidSystem, setRaidSystem] = useState("pf2e");
  const [raidType, setRaidType] = useState("alliance");
  const [dungeonWinCondition, setDungeonWinCondition] = useState("least_survivors");
  const [raidOpen, setRaidOpen] = useState(false);
  const [raidModePickerOpen, setRaidModePickerOpen] = useState(false);
  const [joinRaidOpen, setJoinRaidOpen] = useState(false);
  const [joinRaidCode, setJoinRaidCode] = useState("");
  const [gameShows, setGameShows] = useState([]);
  const [hostShowOpen, setHostShowOpen] = useState(false);
  const [showName, setShowName] = useState("");
  const [showMode, setShowMode] = useState("single_table");
  const [showTargetId, setShowTargetId] = useState("");
  const [showScoringMode, setShowScoringMode] = useState("individual");
  const [showRaidBranches, setShowRaidBranches] = useState([]);
  const [showBranchId, setShowBranchId] = useState("");
  const [showNewTableName, setShowNewTableName] = useState("");
  const [creatingShowTable, setCreatingShowTable] = useState(false);
  const [showMyExistingBranchId, setShowMyExistingBranchId] = useState("");
  const [unreadWhispers, setUnreadWhispers] = useState(0);
  const [joinShowOpen, setJoinShowOpen] = useState(false);
  const [joinShowCode, setJoinShowCode] = useState("");
  const [gameShowIntroOpen, setGameShowIntroOpen] = useState(false);

  const loadGames = async () => {
    const { data } = await api.get("/games");
    setGames(data);
  };

  const loadRaids = async () => {
    const { data } = await api.get("/raids");
    setRaids(data);
  };

  const loadGameShows = async () => {
    const { data } = await api.get("/game-shows/mine");
    setGameShows(data);
  };

  useEffect(() => {
    loadGames();
    loadRaids();
    loadGameShows();
    api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => setBilling(null));
    const loadUnreadWhispers = () => api.get("/games/unread-whispers-count").then((r) => setUnreadWhispers(r.data.count)).catch(() => {});
    loadUnreadWhispers();
    document.addEventListener("visibilitychange", loadUnreadWhispers);
    return () => document.removeEventListener("visibilitychange", loadUnreadWhispers);
  }, []);

  const createGame = async () => {
    if (!name.trim()) return toast.error("Give your game a name");
    try {
      const { data } = await api.post("/games", { name, grid_type: gridType, system, variant_rules: variantRules });
      setOpen(false);
      navigate(`/game/${data.id}`);
    } catch (err) {
      if (err.response?.status === 402) {
        setOpen(false);
        toast.error("Start your free GM trial to create a new game");
        navigate("/billing");
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onNewGameClick = () => {
    if (billing && !billing.has_gm_entitlement) {
      navigate("/billing");
      return;
    }
    setOpen(true);
  };

  const joinGame = async () => {
    if (!joinCode.trim()) return toast.error("Enter an invite code");
    try {
      const { data } = await api.post(`/games/join/${joinCode.trim()}`, {});
      navigate(`/game/${data.game.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyInvite = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${code}`);
    toast.success("Invite link copied");
  };

  const renewAndReopen = async (gameId) => {
    setRenewingId(gameId);
    try {
      const { data: b } = await api.get("/billing/me");
      const { data } = await api.post("/payments/checkout", { plan: b.last_plan || "monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout — try the Billing page instead.");
      setRenewingId(null);
    }
  };

  const onNewRaidClick = () => {
    if (billing && !billing.has_gm_entitlement) {
      toast.error("Raids need an active GM Pass for the Master GM.");
      navigate("/billing");
      return;
    }
    setRaidModePickerOpen(true);
  };

  const onPickRaidMode = (mode) => {
    setRaidType(mode);
    setRaidModePickerOpen(false);
    setRaidOpen(true);
  };

  const createRaid = async () => {
    if (!raidName.trim()) return toast.error("Give your Raid a name");
    try {
      const { data } = await api.post("/raids", { name: raidName, system: raidSystem, raid_type: raidType, dungeon_crawl_win_condition: dungeonWinCondition });
      setRaidOpen(false);
      navigate(`/raid/${data.id}`);
    } catch (err) {
      if (err.response?.status === 402) {
        setRaidOpen(false);
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "You need a Raid Pass to start a Raid");
        navigate("/billing");
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const joinRaid = async () => {
    if (!joinRaidCode.trim()) return toast.error("Enter a Raid invite code");
    try {
      const { data } = await api.post(`/raids/join/${joinRaidCode.trim()}`);
      setJoinRaidOpen(false);
      navigate(`/raid/${data.raid.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onNewGameShowClick = () => {
    setShowTargetId("");
    setGameShowIntroOpen(true);
  };

  const hostGameShow = async () => {
    if (!showName.trim()) return toast.error("Give your Game Show a name");
    if (showMode !== "standalone" && !showTargetId) return toast.error(showMode === "single_table" ? "Pick a table you GM" : "Pick a Raid you're the Master GM of");
    try {
      const payload = { name: showName, mode: showMode, scoring_mode: showScoringMode };
      if (showMode === "single_table") payload.game_id = showTargetId;
      else if (showMode === "raid") {
        payload.raid_id = showTargetId;
        if (showBranchId) payload.branch_id = showBranchId;
      }
      const { data } = await api.post("/game-shows", payload);
      setHostShowOpen(false);
      navigate(`/gameshow/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const pickShowTarget = async (id) => {
    setShowTargetId(id);
    setShowBranchId("");
    setShowRaidBranches([]);
    setShowMyExistingBranchId("");
    if (showMode === "raid" && id) {
      try {
        const { data } = await api.get(`/raids/${id}`);
        setShowRaidBranches(data.branches || []);
        // If the Master GM already has their own Branch on this Raid, pre-select it — the
        // backend only allows one Branch per member, so offering the "make a new one" input
        // here would just fail with a confusing toast.
        if (data.my_branch_id) {
          setShowMyExistingBranchId(data.my_branch_id);
          setShowBranchId(data.my_branch_id);
        }
      } catch {}
    }
  };

  const makeShowTable = async () => {
    if (!showNewTableName.trim()) return toast.error("Give the new Table a name");
    if (!showTargetId) return;
    setCreatingShowTable(true);
    try {
      const { data } = await api.post(`/raids/${showTargetId}/branches`, { name: showNewTableName.trim() });
      setShowRaidBranches((b) => [...b, data.branch]);
      setShowBranchId(data.branch.id);
      setShowNewTableName("");
      toast.success("Table created for this Game Show");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreatingShowTable(false);
    }
  };

  const joinGameShow = async () => {
    if (!joinShowCode.trim()) return toast.error("Enter a Game Show invite code");
    try {
      const { data } = await api.post(`/game-shows/join/${joinShowCode.trim()}`, {});
      setJoinShowOpen(false);
      navigate(`/gameshow/${data.show_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const ownGmGames = games.filter((g) => g.my_role === "gm");
  const masterGmRaids = raids.filter((r) => r.my_role === "master_gm");

  const ownGames = games.filter((g) => !g.via_raid);

  return (
    <div className="relative min-h-screen text-[#f1e0c5] bg-[#151613] bg-cover bg-center bg-fixed" style={{ backgroundImage: `url('${DASHBOARD_BG_URL}')` }}>
      <div className="fixed inset-0 bg-black/70 pointer-events-none" data-testid="dashboard-bg-overlay" />
      <WelcomeIntroModal userId={user?.id} />
      <TrialCountdownBanner billing={billing} className="relative z-10" />
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 border-b border-white/5">
        <Link to="/" className="font-display text-2xl flex items-center gap-2.5">
          <img src="/logo192.png" alt="Call to Arms" className="w-8 h-8 rounded-md" data-testid="brand-logo-icon" />
          Call to Arms<span className="text-[#D90429]">.</span>
        </Link>
        <div className="flex items-center gap-4 flex-wrap justify-end">
          <span className="text-sm text-[#f1e0c5]/60" data-testid="dashboard-user-name">{user?.name}</span>
          <DragonCompanion />
          <WhisperInboxButton unreadCount={unreadWhispers} />
          <Button data-testid="dashboard-billing-button" variant="ghost" size="sm" className="gap-1.5 text-xs uppercase tracking-wider text-[#D4AF37]" onClick={() => navigate("/billing")}>
            <Crown className="w-4 h-4" />
            {billing?.subscription_status && TRIAL_LABEL[billing.subscription_status]
              ? TRIAL_LABEL[billing.subscription_status]
              : billing?.one_time_access_until && new Date(billing.one_time_access_until) > new Date()
              ? "Pass Active"
              : "GM Plan"}
          </Button>
          <Button data-testid="dashboard-my-characters-button" variant="ghost" size="sm" className="gap-1.5 text-xs uppercase tracking-wider" onClick={() => navigate("/characters")}>
            <ScrollText className="w-4 h-4" /> My Characters
          </Button>
          <Button data-testid="dashboard-knights-fee-button" variant="ghost" size="sm" className="gap-1.5 text-xs uppercase tracking-wider" onClick={() => navigate("/knights-fee")}>
            <img src={KF_NAV_ICON_URL} alt="" className="w-4 h-4" /> Knights Fee
          </Button>
          <Button data-testid="dashboard-bazaar-button" variant="ghost" size="sm" className="gap-1.5 text-xs uppercase tracking-wider" onClick={() => navigate("/bazaar")}>
            <Store className="w-4 h-4" /> Bazaar
          </Button>
          <Button data-testid="dashboard-assets-button" variant="ghost" size="sm" className="gap-1.5 text-xs uppercase tracking-wider" onClick={() => navigate("/assets")}>
            <Images className="w-4 h-4" /> Assets
          </Button>
          {user?.is_platform_admin && (
            <Button data-testid="dashboard-admin-button" variant="ghost" size="sm" className="gap-1.5 text-xs uppercase tracking-wider" onClick={() => navigate("/admin")}>
              <Ticket className="w-4 h-4" /> Admin
            </Button>
          )}
          <Button data-testid="dashboard-settings-button" variant="ghost" size="icon" onClick={() => navigate("/settings")} title="Settings">
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </nav>

      <div className="relative z-10 px-6 md:px-12 py-10 max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <h1 className="font-display text-4xl mb-2">Your Games</h1>
            <p className="text-sm text-[#f1e0c5]/50">Create a room as GM or join with an invite code.</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <div className="flex gap-2">
              <Input data-testid="dashboard-join-code-input" placeholder="Invite code" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} className="bg-black/30 border-white/10 w-40" />
              <Button data-testid="dashboard-join-button" variant="secondary" onClick={joinGame} className="rounded-full">Join</Button>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <Button data-testid="dashboard-create-game-button" onClick={onNewGameClick} className="rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-wider text-sm gap-2">
                <Plus className="w-4 h-4" /> New Game
              </Button>
              <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5]">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Create a Game Room</DialogTitle>
                  <DialogDescription>Name your table and pick a grid type to get started.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <Input data-testid="create-game-name-input" placeholder="Game name (e.g. Abomination Vaults)" value={name} onChange={(e) => setName(e.target.value)} className="bg-black/30 border-white/10" />
                  <Select value={gridType} onValueChange={setGridType}>
                    <SelectTrigger data-testid="create-game-grid-select" className="bg-black/30 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="square">Square Grid</SelectItem>
                      <SelectItem value="hex">Hex Grid</SelectItem>
                      <SelectItem value="gridless">Gridless</SelectItem>
                    </SelectContent>
                  </Select>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-2">Game System</p>
                    <Select value={system} onValueChange={(v) => { setSystem(v); setVariantRules({}); }}>
                      <SelectTrigger data-testid="create-game-system-select" className="bg-black/30 border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem data-testid="create-game-system-pf2e" value="pf2e">Pathfinder 2e (built-in rules)</SelectItem>
                        <SelectItem data-testid="create-game-system-dnd5e" value="dnd5e">Dungeons & Dragons 5e (auto ability mods & proficiency)</SelectItem>
                        <SelectItem data-testid="create-game-system-homebrew" value="homebrew">Homebrew (design your own sheet & rules)</SelectItem>
                      </SelectContent>
                    </Select>
                    {system === "homebrew" && (
                      <p className="text-[10px] text-[#f1e0c5]/40 mt-1.5">You'll get a Homebrew Ruleset builder in GM Tools to toggle rule modules, add custom fields, and arrange the sheet layout.</p>
                    )}
                    {system === "dnd5e" && (
                      <p className="text-[10px] text-[#f1e0c5]/40 mt-1.5">Ability modifiers, proficiency bonus, and save/skill totals are automated. A weapon &amp; spell compendium is preloaded — AC and other details are entered manually.</p>
                    )}
                  </div>
                  <VariantRulesEditor system={system} value={variantRules} onChange={setVariantRules} />
                  <Button data-testid="create-game-submit-button" onClick={createGame} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-wider text-sm">
                    Create Room
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button data-testid="dashboard-start-raid-button" onClick={onNewRaidClick} className="rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab] uppercase tracking-wider text-sm gap-2">
              <Shield className="w-4 h-4" /> Start a Raid
            </Button>
            <Dialog open={raidOpen} onOpenChange={setRaidOpen}>
              <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5]">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Start a Raid</DialogTitle>
                  <DialogDescription>A brand-new Master Map table is created for you. Generals join with just your Raid invite code and self-serve their own Branch of scenes inside it — no table of their own needed.</DialogDescription>
                </DialogHeader>
                <p className="text-xs text-[#3B82F6]" data-testid="create-raid-pass-balance-note">
                  Free with your GM Pass — Generals with their own GM Pass/trial join free too.{" "}
                  {billing?.raid_passes_available > 0
                    ? `You have ${billing.raid_passes_available} Raid Pass${billing.raid_passes_available > 1 ? "es" : ""} available if you want to bring in Generals who don't have a Pass (up to 10 per Pass).`
                    : "Grab a $5 Raid Pass from Billing if you want to bring in Generals who don't have a Pass (up to 10 per Pass)."}
                </p>
                <div className="space-y-4 pt-2">
                  <Input data-testid="create-raid-name-input" placeholder="Raid name (e.g. The Dragon's Fall Campaign)" value={raidName} onChange={(e) => setRaidName(e.target.value)} className="bg-black/30 border-white/10" />
                  <Select value={raidSystem} onValueChange={setRaidSystem}>
                    <SelectTrigger data-testid="create-raid-system-select" className="bg-black/30 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pf2e">Pathfinder 2e (built-in rules)</SelectItem>
                      <SelectItem value="dnd5e">Dungeons & Dragons 5e</SelectItem>
                      <SelectItem value="homebrew">Homebrew</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center justify-between bg-black/20 border border-white/10 rounded-lg px-3 py-2.5" data-testid="create-raid-selected-mode">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-[#f1e0c5]/40">Raid Mode</p>
                      <p className="text-sm font-display">{RAID_MODE_LABELS[raidType]}</p>
                    </div>
                    <Button data-testid="create-raid-change-mode-button" size="sm" variant="secondary" className="rounded-full h-7 text-xs" onClick={() => { setRaidOpen(false); setRaidModePickerOpen(true); }}>
                      Change
                    </Button>
                  </div>
                  {raidType === "dungeon_crawl" && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-2">Win Condition — locked in once you found the Raid</p>
                      <Select value={dungeonWinCondition} onValueChange={setDungeonWinCondition}>
                        <SelectTrigger data-testid="create-raid-dungeon-win-condition-select" className="bg-black/30 border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="least_survivors">Least Survivors Wins</SelectItem>
                          <SelectItem value="most_survivors">Most Survivors Wins</SelectItem>
                          <SelectItem value="most_treasure">Most Treasure Wins</SelectItem>
                          <SelectItem value="timed_race">Timed Race — Furthest Progress Wins</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button data-testid="create-raid-submit-button" onClick={createRaid} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab] uppercase tracking-wider text-sm">
                    Found the Raid
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <RaidModePickerModal open={raidModePickerOpen} onOpenChange={setRaidModePickerOpen} onSelect={onPickRaidMode} />
            <GameShowIntroModal open={gameShowIntroOpen} onOpenChange={setGameShowIntroOpen} onHost={() => { setGameShowIntroOpen(false); setHostShowOpen(true); }} />
            <Dialog open={joinRaidOpen} onOpenChange={setJoinRaidOpen}>
              <Button data-testid="dashboard-join-raid-button" variant="secondary" onClick={() => setJoinRaidOpen(true)} className="rounded-full gap-2">
                <Shield className="w-4 h-4" /> Join a Raid
              </Button>
              <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5]">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Join a Raid</DialogTitle>
                  <DialogDescription>Enter the Master GM's Raid invite code — you'll land in the Raid Lobby as a General and can self-serve your own Branch there.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <Input data-testid="join-raid-code-input" placeholder="Raid invite code" value={joinRaidCode} onChange={(e) => setJoinRaidCode(e.target.value)} className="bg-black/30 border-white/10" />
                  <Button data-testid="join-raid-submit-button" onClick={joinRaid} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab] uppercase tracking-wider text-sm">
                    Join Raid
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <button
              data-testid="dashboard-host-gameshow-button"
              onClick={onNewGameShowClick}
              title="Host a Game Show"
              className="rounded-full w-11 h-11 flex items-center justify-center bg-[#3F9142] hover:bg-[#357a39] text-white transition-colors duration-200 shrink-0"
            >
              <Disc3 className="w-5 h-5" />
            </button>
            <Dialog open={hostShowOpen} onOpenChange={setHostShowOpen}>
              <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5]">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl flex items-center gap-2"><Disc3 className="w-5 h-5 text-[#3F9142]" /> Host a Game Show</DialogTitle>
                  <DialogDescription>A Wheel Spinner, Trivia board, and Item Collection round — GM-narrated or auto-resolved, your call.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <Input data-testid="host-gameshow-name-input" placeholder="Game Show name (e.g. Wheel of Misfortune)" value={showName} onChange={(e) => setShowName(e.target.value)} className="bg-black/30 border-white/10" />
                  <Select value={showMode} onValueChange={(v) => { setShowMode(v); setShowTargetId(""); setShowBranchId(""); setShowRaidBranches([]); }}>
                    <SelectTrigger data-testid="host-gameshow-mode-select" className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single_table">Single Table (one of your games)</SelectItem>
                      <SelectItem value="raid">Raid-wide (one of your Raids)</SelectItem>
                      <SelectItem value="standalone">Standalone (no campaign needed)</SelectItem>
                    </SelectContent>
                  </Select>
                  {showMode !== "standalone" && (
                    <Select value={showTargetId} onValueChange={pickShowTarget}>
                      <SelectTrigger data-testid="host-gameshow-target-select" className="bg-black/30 border-white/10">
                        <SelectValue placeholder={showMode === "single_table" ? "Pick a table you GM…" : "Pick a Raid you Master GM…"} />
                      </SelectTrigger>
                      <SelectContent>
                        {(showMode === "single_table" ? ownGmGames : masterGmRaids).map((x) => (
                          <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {showMode === "standalone" && (
                    <p className="text-[10px] text-[#f1e0c5]/40">Not linked to any table or Raid — pure Wheel/Trivia/Items fun on its own. Contestants join with just the invite code, no character sheet required.</p>
                  )}
                  {showMode !== "standalone" && (showMode === "single_table" ? ownGmGames : masterGmRaids).length === 0 && (
                    <p className="text-[10px] text-[#f1e0c5]/40">{showMode === "single_table" ? "You need a game you GM first." : "You need a Raid you're the Master GM of first."}</p>
                  )}
                  {showMode === "raid" && showTargetId && (
                    <div className="space-y-1.5">
                      <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50">Table for this Show (optional)</p>
                      <div className="flex gap-2">
                        <Select value={showBranchId} onValueChange={setShowBranchId}>
                          <SelectTrigger data-testid="host-gameshow-branch-select" className="bg-black/30 border-white/10 flex-1">
                            <SelectValue placeholder="Master's own Table" />
                          </SelectTrigger>
                          <SelectContent>
                            {showRaidBranches.map((b) => (
                              <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!showMyExistingBranchId && (
                          <>
                            <Input
                              data-testid="host-gameshow-new-table-name-input"
                              placeholder="Or name a new Table…"
                              value={showNewTableName}
                              onChange={(e) => setShowNewTableName(e.target.value)}
                              className="bg-black/30 border-white/10 flex-1"
                            />
                            <Button data-testid="host-gameshow-new-table-button" type="button" variant="secondary" disabled={creatingShowTable} onClick={makeShowTable} className="rounded-full shrink-0">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                      <p className="text-[10px] text-[#f1e0c5]/40" data-testid="host-gameshow-table-hint">
                        {showMyExistingBranchId
                          ? "You already have your own Table on this Raid — it's pre-selected above, or pick any other Table from the dropdown."
                          : "Give this Game Show its own dedicated map, separate from your Master Table or any other Table already running."}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-2">Scoring</p>
                    <Select value={showScoringMode} onValueChange={setShowScoringMode}>
                      <SelectTrigger data-testid="host-gameshow-scoring-select" className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual Contestants</SelectItem>
                        <SelectItem value="team">Teams</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="host-gameshow-submit-button" onClick={hostGameShow} className="w-full rounded-full bg-[#3F9142] hover:bg-[#357a39] uppercase tracking-wider text-sm">
                    Start the Show
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={joinShowOpen} onOpenChange={setJoinShowOpen}>
              <Button data-testid="dashboard-join-gameshow-button" variant="secondary" onClick={() => setJoinShowOpen(true)} className="rounded-full gap-2">
                <Disc3 className="w-4 h-4 text-[#3F9142]" /> Join Gameshow
              </Button>
              <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5]">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Join a Game Show</DialogTitle>
                  <DialogDescription>Enter the host's Game Show invite code to step up as a contestant.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <Input data-testid="join-gameshow-code-input" placeholder="Game Show invite code" value={joinShowCode} onChange={(e) => setJoinShowCode(e.target.value)} className="bg-black/30 border-white/10" />
                  <Button data-testid="join-gameshow-submit-button" onClick={joinGameShow} className="w-full rounded-full bg-[#3F9142] hover:bg-[#357a39] uppercase tracking-wider text-sm">
                    Join Show
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {ownGames.length === 0 && (
          <div className="text-center py-20 text-[#f1e0c5]/40" data-testid="dashboard-empty-state">
            <Swords className="w-10 h-10 mx-auto mb-4 opacity-40" />
            No games yet. Create one, or join with an invite code.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ownGames.map((g) => (
            <div key={g.id} data-testid={`game-card-${g.id}`} className={`p-6 rounded-2xl bg-black/40 border transition-colors duration-200 ${g.is_trial_locked ? "border-[#D90429]/50" : "border-white/10 hover:border-[#D4AF37]/40"}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#D90429]/20 text-[#D90429]">{g.my_role}</span>
                {g.my_role === "gm" && (
                  <button data-testid={`copy-invite-${g.id}`} onClick={() => copyInvite(g.invite_code)} className="text-[#f1e0c5]/40 hover:text-[#D4AF37] transition-colors duration-200">
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              </div>
              <ExpandableTitle text={g.name} testId={`game-card-title-${g.id}`} />
              <p className="text-xs text-[#f1e0c5]/40 mb-4 uppercase tracking-wider">
                {g.grid_type} grid · Invite {g.invite_code} {g.system === "homebrew" && <span className="text-[#D4AF37]">· Homebrew</span>} {g.system === "dnd5e" && <span className="text-[#D4AF37]">· D&D 5e</span>}
              </p>
              {g.is_trial_locked && (
                <p data-testid={`game-card-trial-locked-${g.id}`} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#D90429] mb-3">
                  <Lock className="w-3 h-3" /> Trial Ended — Subscribe to Reopen
                </p>
              )}
              <Button
                data-testid={`enter-game-${g.id}`}
                onClick={() => (g.is_trial_locked ? renewAndReopen(g.id) : navigate(`/game/${g.id}`))}
                disabled={renewingId === g.id}
                variant="secondary"
                className="w-full rounded-full"
              >
                {g.is_trial_locked ? (renewingId === g.id ? "Opening Checkout…" : "Renew & Reopen") : "Enter Room"}
              </Button>
            </div>
          ))}
        </div>

        {raids.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display text-3xl mb-6 flex items-center gap-2"><Shield className="w-6 h-6 text-[#3B82F6]" /> Your Raids</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {raids.map((r) => (
                <div key={r.id} data-testid={`raid-card-${r.id}`} className={`p-6 rounded-2xl bg-black/40 border transition-colors duration-200 ${r.is_trial_locked ? "border-[#D90429]/50" : "border-[#1E3A8A]/30 hover:border-[#3B82F6]/60"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#1E3A8A]/30 text-[#3B82F6]">{r.my_role === "master_gm" ? "Master GM" : "General"}</span>
                    {r.my_role === "master_gm" && (
                      <button data-testid={`copy-raid-invite-${r.id}`} onClick={() => copyInvite(r.invite_code)} className="text-[#f1e0c5]/40 hover:text-[#3B82F6] transition-colors duration-200">
                        <Copy className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <ExpandableTitle text={r.name} testId={`raid-card-title-${r.id}`} />
                  <p className="text-xs text-[#f1e0c5]/40 mb-4 uppercase tracking-wider">Raid Code {r.invite_code}{r.raid_type === "tournament" && <span className="text-[#D4AF37]"> · Tournament</span>}{r.raid_type === "party" && <span className="text-[#D4AF37]"> · Party</span>}{r.raid_type === "bbebbeg" && <span className="text-[#D90429]"> · BBEBBEG</span>}{r.raid_type === "dungeon_crawl" && <span className="text-[#D90429]"> · Dungeon Crawl</span>}</p>
                  {r.is_trial_locked && (
                    <p data-testid={`raid-card-trial-locked-${r.id}`} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#D90429] mb-3">
                      <Lock className="w-3 h-3" /> Master GM's Trial Ended — Tables Locked
                    </p>
                  )}
                  <Button data-testid={`enter-raid-${r.id}`} onClick={() => navigate(`/raid/${r.id}`)} variant="secondary" className="w-full rounded-full">
                    Enter Raid Dashboard
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        {gameShows.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display text-3xl mb-6 flex items-center gap-2"><Disc3 className="w-6 h-6 text-[#3F9142]" /> Your Game Shows</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {gameShows.map((s) => (
                <div key={s.id} data-testid={`gameshow-card-${s.id}`} className="p-6 rounded-2xl bg-black/40 border border-[#3F9142]/30 hover:border-[#3F9142]/60 transition-colors duration-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#3F9142]/25 text-[#3F9142]">{s.my_role === "host" ? "Host" : "Contestant"}</span>
                    {s.my_role === "host" && (
                      <button data-testid={`copy-gameshow-invite-${s.id}`} onClick={() => { navigator.clipboard.writeText(s.invite_code); toast.success("Game Show invite code copied"); }} className="text-[#f1e0c5]/40 hover:text-[#3F9142] transition-colors duration-200">
                        <Copy className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <ExpandableTitle text={s.name} testId={`gameshow-card-title-${s.id}`} />
                  <p className="text-xs text-[#f1e0c5]/40 mb-4 uppercase tracking-wider">Code {s.invite_code} · {s.mode === "raid" ? "Raid-wide" : "Single Table"}</p>
                  <Button data-testid={`enter-gameshow-${s.id}`} onClick={() => navigate(`/gameshow/${s.id}`)} variant="secondary" className="w-full rounded-full">
                    Enter Game Show
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 pb-4 text-center">
          <a href="mailto:support@wwwwhynot.com" data-testid="support-email-link" className="text-xs text-[#f1e0c5]/30 hover:text-[#D4AF37] transition-colors duration-200">
            support@wwwwhynot.com
          </a>
        </div>
      </div>
    </div>
  );
}
